/**
 * Backend enum-label maps.
 *
 * The live API at api.ebillbook.kristysoftware.com transports enum-typed fields
 * (`SalesOrder.status`, `Invoice.gstType`, `Payment.mode`, …) as **`int32`**
 * values, but the swagger doesn't list the names. The mappings below are
 * the conventional .NET labels matched against observed sample data; they
 * MUST be confirmed with the backend team and updated when their canonical
 * source becomes available.
 *
 * Usage:
 * ```ts
 * import { enumLabel } from '@kss/api';
 * <StatusBadge status={enumLabel('SalesOrderStatus', order.status).toLowerCase()} />
 * ```
 *
 * Unknown values fall back to `Status #N` so a UI surface never hard-crashes
 * if the backend introduces a new enum value the frontend hasn't seen yet.
 */

export const SalesOrderStatusLabels = {
  1: 'Draft',
  2: 'Confirmed',
  3: 'Shipped',
  4: 'Delivered',
  5: 'Cancelled',
} as const;
export type SalesOrderStatus = number;

export const SalesOrderChannelLabels = {
  1: 'POS',
  2: 'Online',
  3: 'B2B',
  4: 'Phone',
} as const;
export type SalesOrderChannel = number;

export const PaymentStatusLabels = {
  1: 'Unpaid',
  2: 'Partial',
  3: 'Paid',
  4: 'Refunded',
} as const;
export type PaymentStatus = number;

export const InvoiceStatusLabels = {
  1: 'Draft',
  2: 'Issued',
  3: 'Paid',
  4: 'PartiallyPaid',
  5: 'Cancelled',
  6: 'Overdue',
} as const;
export type InvoiceStatus = number;

export const GstTypeLabels = {
  1: 'Regular',
  2: 'IGST',
  3: 'Composition',
  4: 'NonGst',
  5: 'Exempted',
} as const;
export type GstType = number;

export const PaymentModeLabels = {
  1: 'Cash',
  2: 'Card',
  3: 'UPI',
  4: 'NetBanking',
  5: 'Cheque',
  6: 'Wallet',
  7: 'COD',
} as const;
export type PaymentMode = number;

export const AttendanceStatusLabels = {
  1: 'Present',
  2: 'Absent',
  3: 'Leave',
  4: 'HalfDay',
  5: 'Holiday',
  6: 'WeeklyOff',
} as const;
export type AttendanceStatus = number;

export const PayrollStatusLabels = {
  1: 'Draft',
  2: 'Approved',
  3: 'Paid',
  4: 'Cancelled',
} as const;
export type PayrollStatus = number;

export const ExpensePaymentModeLabels = PaymentModeLabels;

export const OrderReturnTypeLabels = {
  1: 'SalesOrder',
  2: 'PurchaseOrder',
} as const;
export type OrderReturnType = number;

export const AccountTypeLabels = {
  1: 'Asset',
  2: 'Liability',
  3: 'Equity',
  4: 'Income',
  5: 'Expense',
  6: 'Bank',
  7: 'CashInHand',
} as const;
export type AccountType = number;

export const JournalEntryStatusLabels = {
  1: 'Draft',
  2: 'Posted',
  3: 'Reversed',
} as const;
export type JournalEntryStatus = number;

export const StockMovementTypeLabels = {
  1: 'In',
  2: 'Out',
  3: 'Transfer',
  4: 'Adjustment',
  5: 'Return',
} as const;
export type StockMovementType = number;

export const UserStatusLabels = {
  1: 'Active',
  2: 'Disabled',
  3: 'Locked',
  4: 'PendingVerification',
} as const;
export type UserStatus = number;

export const LicenseStatusLabels = {
  1: 'Active',
  2: 'Trial',
  3: 'Expired',
  4: 'Suspended',
  5: 'Cancelled',
} as const;
export type LicenseStatus = number;

export const TenantStatusLabels = {
  1: 'Active',
  2: 'Suspended',
  3: 'Deactivated',
} as const;
export type TenantStatus = number;

export const TenantDomainStatusLabels = {
  1: 'Active',
  2: 'Inactive',
} as const;
export type TenantDomainStatus = number;

export const PurchaseOrderStatusLabels = {
  1: 'Draft',
  2: 'Sent',
  3: 'Received',
  4: 'PartiallyReceived',
  5: 'Cancelled',
} as const;
export type PurchaseOrderStatus = number;

export const EmployeeStatusLabels = {
  1: 'Active',
  2: 'OnLeave',
  3: 'Terminated',
  4: 'Resigned',
} as const;
export type EmployeeStatus = number;

export const SubscriptionPlanTierLabels = {
  0: 'Free',
  1: 'Pro',
  2: 'Business',
  3: 'Enterprise',
} as const;
export type SubscriptionPlanTier = number;

export const PayslipStatusLabels = PayrollStatusLabels;

/* ---------------------------------------------------------------------- */
/* Lookup helper                                                            */
/* ---------------------------------------------------------------------- */

export type EnumKind =
  | 'SalesOrderStatus' | 'SalesOrderChannel' | 'PaymentStatus' | 'InvoiceStatus'
  | 'GstType' | 'PaymentMode' | 'AttendanceStatus' | 'PayrollStatus'
  | 'ExpensePaymentMode' | 'OrderReturnType' | 'AccountType' | 'JournalEntryStatus'
  | 'StockMovementType' | 'UserStatus' | 'LicenseStatus' | 'TenantStatus'
  | 'TenantDomainStatus' | 'PurchaseOrderStatus' | 'EmployeeStatus'
  | 'SubscriptionPlanTier' | 'PayslipStatus';

const REGISTRY: Record<EnumKind, Readonly<Record<number, string>>> = {
  SalesOrderStatus: SalesOrderStatusLabels,
  SalesOrderChannel: SalesOrderChannelLabels,
  PaymentStatus: PaymentStatusLabels,
  InvoiceStatus: InvoiceStatusLabels,
  GstType: GstTypeLabels,
  PaymentMode: PaymentModeLabels,
  AttendanceStatus: AttendanceStatusLabels,
  PayrollStatus: PayrollStatusLabels,
  ExpensePaymentMode: ExpensePaymentModeLabels,
  OrderReturnType: OrderReturnTypeLabels,
  AccountType: AccountTypeLabels,
  JournalEntryStatus: JournalEntryStatusLabels,
  StockMovementType: StockMovementTypeLabels,
  UserStatus: UserStatusLabels,
  LicenseStatus: LicenseStatusLabels,
  TenantStatus: TenantStatusLabels,
  TenantDomainStatus: TenantDomainStatusLabels,
  PurchaseOrderStatus: PurchaseOrderStatusLabels,
  EmployeeStatus: EmployeeStatusLabels,
  SubscriptionPlanTier: SubscriptionPlanTierLabels,
  PayslipStatus: PayslipStatusLabels,
};

/** Resolve an enum integer to its human label, with `Status #N` fallback. */
export function enumLabel(kind: EnumKind, value: number | null | undefined): string {
  if (value == null) return '—';
  const labels = REGISTRY[kind];
  return (labels as Record<number, string>)[value] ?? `${kind.replace(/([A-Z])/g, ' $1').trim()} #${value}`;
}

/** Inverse — turn a label into an int (case-insensitive). Returns `undefined` when not matched. */
export function enumValue(kind: EnumKind, label: string | null | undefined): number | undefined {
  if (!label) return undefined;
  const target = String(label).toLowerCase();
  const labels = REGISTRY[kind];
  for (const [k, v] of Object.entries(labels)) {
    if (v.toLowerCase() === target) return Number(k);
  }
  return undefined;
}

/** Iterate every (value, label) pair for select-dropdown options. */
export function enumEntries(kind: EnumKind): { value: number; label: string }[] {
  return Object.entries(REGISTRY[kind]).map(([v, l]) => ({ value: Number(v), label: l }));
}
